<template>
  <div
    v-if="isDataGot && errorAxios"
    :key="`model-similar${type}`"
    class="model-similar"
    :class="{ 'model-similar--full': fullWidthMobile }"
  >
    <h2 v-if="!hideTitle" :class="['title', fontClass]">
      {{ title }}
    </h2>
    <div class="slider-list">
      <ui-multiple-swiper
        :class="[isMobile ? 'slider-mobile' : 'slider-desktop']"
        :free-mode="isMobile && fullWidthMobile"
        :space-between="spaceBetween"
      >
        <template v-for="(el, index) in similarShow">
          <ui-multiple-swiper-slide
            v-if="isBot || isFirstInteractive || index < itemCount"
            :key="`${el.id}_${index}_${type}`"
            class="similar-product-wrap"
          >
            <card-model-tile
              v-if="el.type == 'model'"
              :show-more-button="showMoreButton"
              :model="isMobile ? { ...el, additional_images: [] } : el"
              type-style="tile"
              :type="el.type"
              class="card"
              viewed
              :position="index + 1"
              block-type="model-similar"
              :discount-max="el.discount_max"
              :is-carousel="true"
            />
            <card-offer-tile
              v-if="el.type == 'offer'"
              :show-more-button="showMoreButton"
              :model="isMobile ? { ...el, additional_images: [] } : el"
              type-style="tile"
              :type="el.type"
              class="card"
              viewed
              :position="index + 1"
              block-type="model-similar"
            />
          </ui-multiple-swiper-slide>
        </template>
      </ui-multiple-swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ResponseModelSimilar } from '@/interfaces/api';
import { API } from '@/api/api';
import { useInteractiveStore } from '~/stores/interactive';
import { useGlobalStore } from '~/stores/global';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import cardModelTile from '~/components/product-card/productCardTileModel/productCardTileModel.vue';
import cardOfferTile from '~/components/product-card/productCardTileOffer/productCardTileOffer.vue';
import { useAnalyticsStore } from '~/stores/analytics';

const props = defineProps({
  similar: {
    type: Array as () => ResponseModelSimilar,
    default: () => [],
  },
  fullWidthMobile: {
    type: Boolean,
    default: false,
  },
  modelId: {
    type: Number,
    required: true,
  },
  itemCount: {
    type: Number,
    default: 6,
  },
  changedPage: Boolean,
  showMoreButton: Boolean,
  hideTitle: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Похожие товары',
  },
  type: {
    type: String,
    default: '',
  },
  pageData: {
    type: Object as () => any,
    required: true,
  },
  fontClass: {
    type: String,
    default: 'heading-m',
  },
});
const emit = defineEmits(['change-data']);
const urls = {
  similar: `/models/${props.modelId}/similar`,
  similaruserlike: `/models/${props.modelId}/similar`,
  other: `/models/${props.modelId}/other`,
};
const mediaStore = useMediaQueryStore();
const isDataGot = computed(() => {
  if (props.type === 'accessories')
    return props.pageData?.accessories || props?.similar?.length > 0;
  if (props.type === 'other') {
    return props.pageData?.other?.length > 0;
  }
  return props?.similar?.length > 0;
});
const isMobile = computed(
  () =>
    mediaStore.isMobile ||
    mediaStore.isLessEs ||
    mediaStore.isEs ||
    mediaStore.isSm ||
    mediaStore.isMd
);
const spaceBetween = computed(() =>
  mediaStore.isEs || mediaStore.isSm ? 8 : mediaStore.isMd ? 12 : 16
);
const globalStore = useGlobalStore();
const interactiveStore = useInteractiveStore();
const analyticsStore = useAnalyticsStore();
const isFirstInteractive = computed(() => interactiveStore.isFirstInteractive);
const isBot = computed(() => globalStore.isBot);
const similarShow = computed(() => {
  if (props.type === 'accessories' && props.pageData?.accessories?.length)
    return props.pageData?.accessories;
  if (props.type === 'other' && props.pageData?.other?.length)
    return props.pageData?.other;
  return interactiveStore?.isPageSpeedBot
    ? props.similar?.slice(0, 4)
    : props.similar;
});
const errorAxios = ref<boolean | null>(true);

onBeforeMount(() => {
  if (props.type === 'accessories') {
    if (props.pageData?.accessories && props.pageData?.accessories?.length > 1)
      return;

    if (
      !Object.prototype.hasOwnProperty.call(
        props.pageData,
        props.pageData?.accessories
      )
    ) {
      const controller = new AbortController();
      if (!import.meta.env.SSR) {
        globalStore.setCancelToken(controller);
      }
      API.get(`/categories/${props.pageData.model.category.id}/accessories`, {
        params: {
          region_id: globalStore?.region?.id || 1,
          sem: globalStore.sem || undefined,
          device: globalStore.device,
          adult: globalStore.adult,
          split: analyticsStore?.additional?.split,
        },
        signal: controller.signal,
      })
        .then((res) => {
          if (res && res?.data.items && res.data?.items?.length > 0) {
            emit('change-data', {
              key: props.type,
              data: res.data.items,
            });
          }
        })
        .catch((e) => {
          emit('change-data', {
            key: props.type,
            data: [],
          });
          console.error('get accessories error', e);
        });
    }
  } else if (
    props.type &&
    props.pageData &&
    !props.pageData[props.type]?.length &&
    !import.meta.env.SSR &&
    !props.changedPage
  ) {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      globalStore.setCancelToken(controller);
    }
    API.get(urls[props.type], {
      params: {
        region_id: globalStore.region.id,
        sem: globalStore.sem || undefined,
        device: globalStore.device,
        adult: globalStore.adult,
        split: analyticsStore?.additional?.split,
      },
      signal: controller.signal,
    })
      .then((res) => {
        emit('change-data', {
          key: props.type,
          data: res?.data,
        });
      })
      .catch(() => {
        emit('change-data', {
          key: props.type,
          data: [],
        });
        errorAxios.value = false;
      });
  }
});
</script>
<style lang="scss" src="./ModelSimilarScss.scss" scoped></style>
